import { generateSKU } from './skuService';
import { generateCableSVG } from './hybridCableSvgGenerator';
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';
import { getWireTypeDescription } from './wireCompatibilityService';
import { compatibilityMatrix } from './compatibilityMatrix';

/**
 * Bill of Materials (BOM) Generator Service
 * Creates shareable BOM links that can reconstruct cable configurations
 */



/**
 * Generate a complete BOM for a cable configuration
 * @param {Object} config - The cable configuration object
 * @param {string} customName - Optional custom name for the BOM
 * @returns {Object} Complete BOM data
 */
export async function generateBOM(config, customName = null) {
  try {
    const sku = generateSKU(config);
    const bomId = btoa(sku).replace(/[^a-zA-Z0-9]/g, '').substring(0, 12);
    
    // Generate SVG for the BOM
    let svgContent = null;
    try {
      const configWithSku = { ...config, sku };
      svgContent = await generateCableSVG(configWithSku);
    } catch (error) {
      console.warn('Could not generate SVG for BOM:', error);
    }

    const bom = {
      id: bomId,
      sku: sku,
      name: customName || `NLFX Cable - ${sku}`,
      timestamp: Date.now(),
      config: config,
      svgContent: svgContent,
      components: extractComponents(config),
      specifications: extractSpecifications(config),
      metadata: {
        generatedBy: 'NLFX Cable Builder',
        version: '1.0',
        exportDate: new Date().toISOString()
      }
    };

    return bom;
  } catch (error) {
    console.error('Error generating BOM:', error);
    throw new Error('Failed to generate BOM');
  }
}

/**
 * Create a shareable BOM link
 * @param {Object} config - The cable configuration object
 * @param {string} customName - Optional custom name
 * @returns {Promise<Object>} Promise resolving to shareable URL object
 */
export async function createBOMLink(config, customName = null) {
  try {
    const bom = await generateBOM(config, customName);
    
    // Create a compressed, URL-safe version of the BOM
    const compressedBOM = compressBOMForURL(bom);
    
    // Generate the shareable URL
    const baseUrl = window.location.origin;
    const shareableUrl = `${baseUrl}/bom/${bom.id}?data=${compressedBOM}`;
    
    return {
      url: shareableUrl,
      bomId: bom.id,
      sku: bom.sku,
      name: bom.name
    };
  } catch (error) {
    console.error('Error creating BOM link:', error);
    throw new Error('Failed to create BOM link');
  }
}

/**
 * Reconstruct cable configuration from a BOM link
 * @param {string} bomId - The BOM ID
 * @param {string} compressedData - The compressed BOM data
 * @returns {Object} Reconstructed cable configuration
 */
export function reconstructFromBOMLink(bomId, compressedData) {
  try {
    // Decompress the BOM data
    const bom = decompressBOMFromURL(compressedData);
    
    if (!bom || !bom.config) {
      throw new Error('Invalid BOM data');
    }
    
    // Validate the BOM ID matches
    if (bom.id !== bomId) {
      throw new Error('BOM ID mismatch');
    }
    
    // Regenerate the complete BOM with components and specifications
    const completeBom = {
      ...bom,
      components: extractComponents(bom.config),
      specifications: extractSpecifications(bom.config),
      metadata: {
        generatedBy: 'NLFX Cable Builder',
        version: '1.0',
        exportDate: new Date().toISOString(),
        reconstructed: true
      }
    };
    
    // Debug logging removed for production
    
    return {
      config: bom.config,
      bom: completeBom,
      reconstructed: true
    };
  } catch (error) {
    console.error('Error reconstructing from BOM link:', error);
    throw new Error('Failed to reconstruct cable configuration');
  }
}

/**
 * Extract component list from cable configuration
 * @param {Object} config - The cable configuration
 * @returns {Array} List of components
 */
function extractComponents(config) {
  const components = [];
  
  // Add cable type
  components.push({
    type: 'cable',
    category: config.category || 'Audio',
    specification: config.type || 'Unknown',
    quantity: 1,
    description: `${config.category || 'Audio'} ${config.type || 'Unknown'} Cable`
  });

  // Add connectors based on cable style
  if (config.style === 'end-to-end') {
    // Simple end-to-end cable
    if (config.connectors?.endA) {
      components.push({
        type: 'connector',
        position: 'End A',
        specification: getConnectorSpec(config.connectors.endA),
        quantity: 1,
        description: `Connector A: ${getConnectorDescription(config.connectors.endA)}`
      });
    }
    
    if (config.connectors?.endB) {
      components.push({
        type: 'connector',
        position: 'End B',
        specification: getConnectorSpec(config.connectors.endB),
        quantity: 1,
        description: `Connector B: ${getConnectorDescription(config.connectors.endB)}`
      });
    }
  } else if (config.hybridConnectors?.connectors) {
    // Complex cable with multiple connectors
    const connectors = config.hybridConnectors.connectors;
    
    Object.entries(connectors).forEach(([position, connector]) => {
      if (connector && connector.id) {
        components.push({
          type: 'connector',
          position: position,
          specification: getConnectorSpec(connector),
          quantity: 1,
          description: `Connector ${position}: ${getConnectorDescription(connector)}`
        });
      }
    });
  }

  // Add wire gauge for power cables
  if (config.type === 'power' && config.wireGauge?.gauge) {
    components.push({
      type: 'wire',
      specification: `${config.wireGauge.gauge} AWG`,
      color: config.wireGauge.color || 'Black',
      quantity: 1,
      description: `${config.wireGauge.gauge} AWG ${config.wireGauge.color || 'Black'} Wire`
    });
  }

  // Add wire type for non-power cables
  if (config.type !== 'power') {
    const wireType = getCorrectWireType(config);
    if (wireType) {
      components.push({
        type: 'wire',
        specification: wireType,
        quantity: 1,
        description: `${wireType} - ${getWireTypeDescription(wireType)}`
      });
    }
  }

  // Add finishing components
  if (config.finishing) {
    // Helper to get a user-friendly label from a finishing value
    function getFinishingLabel(val, fallback = 'none') {
      if (!val || val === 'none') return fallback;
      if (typeof val === 'string') return val;
      if (typeof val === 'object' && val.type) return val.type;
      return fallback;
    }

    function describeHeatShrinkSelections(finishing) {
      const hs = finishing?.heatShrink;
      if (!hs) return '';
      if (typeof hs === 'string') return hs === 'none' ? '' : hs;
      if (typeof hs === 'object' && hs.type) return hs.type === 'none' ? '' : hs.type;
      const parts = Object.entries(hs)
        .filter(([k, v]) => k !== 'global' && v && v !== 'none')
        .map(([k, v]) => `${k}: ${v}`);
      if (parts.length) return parts.join('; ');
      if (hs.global && hs.global !== 'none') return hs.global;
      return '';
    }

    const hsSummary = describeHeatShrinkSelections(config.finishing);
    if (hsSummary) {
      components.push({
        type: 'finishing',
        specification: 'Heat Shrink',
        variant: hsSummary,
        quantity: 1,
        description: `Heat Shrink: ${hsSummary}`
      });
    }

    if (config.finishing.customHeatShrink) {
      const ch = config.finishing.customHeatShrink;
      let detail = '';
      if (ch.mode === 'logo' && ch.logo?.publicUrl) {
        detail = `Logo: ${ch.logo.publicUrl}`;
      } else if (ch.mode === 'text') {
        detail = `Text: "${ch.line1 || ''}" / "${ch.line2 || ''}"`;
      }
      components.push({
        type: 'finishing',
        specification: 'Custom printed heat shrink (2")',
        variant: `${ch.tubeColor || 'black'} tube / ${ch.ribbonColor || 'white'} text`,
        quantity: 1,
        description: detail ? `Custom HS — ${detail}` : 'Custom HS'
      });
    }
    if (config.finishing.techFlex && getFinishingLabel(config.finishing.techFlex) !== 'none') {
      components.push({
        type: 'finishing',
        specification: 'TechFlex',
        variant: getFinishingLabel(config.finishing.techFlex),
        quantity: 1,
        description: `TechFlex: ${getFinishingLabel(config.finishing.techFlex, 'none')}`
      });
    }
    if (config.finishing.labels && getFinishingLabel(config.finishing.labels) !== 'none') {
      components.push({
        type: 'finishing',
        specification: 'Labels',
        variant: getFinishingLabel(config.finishing.labels),
        quantity: 1,
        description: `Labels: ${getFinishingLabel(config.finishing.labels, 'none')}`
      });
    }
  }

  return components;
}

/**
 * Extract technical specifications from cable configuration
 * @param {Object} config - The cable configuration
 * @returns {Object} Technical specifications
 */
function extractSpecifications(config) {
  // Check if this is a hybrid cable (either by type or style)
  const isHybridCable = config.type === 'hybrid' || 
                       config.style === 'hybrid' || 
                       config.style === 'hybrid-power-audio-audio';

  let cableCategory = config.category;
  let cableType = config.type || 'Unknown';
  let cableStyle = config.style || 'end-to-end';

  // For hybrid cables, override all three fields to be "Hybrid"
  if (isHybridCable) {
    cableType = 'Hybrid';
    cableCategory = 'Hybrid';
    cableStyle = 'Hybrid';
  } else {
    // Original logic for non-hybrid cables
    if (!cableCategory) {
      if (config.style === 'wye') cableCategory = 'Wye';
      else if (config.style === 'zip' || config.style === 'zip-audio-audio') cableCategory = 'Zip';
      else if (config.type === 'power') cableCategory = 'Power';
      else cableCategory = 'Audio';
    }
  }
  
  const specs = {
    cableType: cableType,
    cableCategory: cableCategory,
    cableStyle: cableStyle,
    length: {
      value: config.length || 0,
      unit: 'feet'
    }
  };

  // Add length specifications for complex cables
  if (config.hybridConnectors?.lengths) {
    specs.lengths = config.hybridConnectors.lengths;
  }

  // Add wire specifications for power cables
  if (config.type === 'power' && config.wireGauge) {
    specs.wireGauge = config.wireGauge;
  }

  // Add wire type specifications for non-power cables and hybrid cables
  if (config.type !== 'power' || isHybridCable) {
    const wireType = getCorrectWireType(config);
    if (wireType) {
      specs.wireType = wireType;
    }
  }

  // Add finishing specifications
  if (config.finishing) {
    specs.finishing = config.finishing;
  }

  return specs;
}

/**
 * Get the correct wire type based on cable style and configuration
 * @param {Object} config - The cable configuration
 * @returns {string} Correct wire type
 */
function getCorrectWireType(config) {
  // Handle different cable styles
  if (config.style === 'zip' || config.style === 'zip-audio-audio') {
    // Check if this is actually a hybrid cable with wrong style
    if (config.type === 'hybrid') {
      // This is a hybrid cable with wrong style - use PSH
      return config.audioWireType || 'PSH';
    } else {
      // True zip cables use PSZ
      return 'PSZ';
    }
  } else if (config.style === 'wye') {
    return 'PSZ'; // Wye cables always use PSZ (Zip cable type)
  } else if (config.style === 'hybrid' || config.style === 'hybrid-power-audio-audio') {
    // Hybrid cables always use PSH, but check config first for consistency
    return config.audioWireType || 'PSH'; // Hybrid cables always use PSH
  } else if (config.type === 'power') {
    // Power cables use PSP + wire gauge
    const wireGauge = config.wireGauge?.gauge || config.powerWireGauge;
    if (wireGauge) {
      const awg = wireGauge.replace('AWG', ''); // Remove 'AWG' suffix if present
      return `PSP${awg}`;
    } else {
      return 'PSP'; // Default power cable without specific gauge
    }
  } else if (config.type === 'speaker') {
    // Speaker cables use PSS + wire gauge
    const wireGauge = config.wireGauge?.gauge || config.speakerWireGauge;
    if (wireGauge) {
      const awg = wireGauge.replace('AWG', ''); // Remove 'AWG' suffix if present
      return `PSS${awg}`;
    } else {
      // Use the wire type from config
      return config.wireType || 'PSS16';
    }
  } else {
    // For standard audio cables, check if this is a speaker or power cable based on wire type
    // If wire type starts with PSS, treat it as a speaker cable
    if (config.wireType && config.wireType.startsWith('PSS')) {
      return config.wireType; // Use PSS14, PSS12, PSS16 directly
    } else if (config.wireType && config.wireType.startsWith('PSP')) {
      return config.wireType; // Use PSP14, PSP12, PSP16 directly
    } else {
      // For other audio cables, use the wire type from config
      // Check if this is a DMX cable based on wire type
      if (config.wireType === 'PSDMX') {
        return 'PSDMX';
      }
      return config.wireType || config.audioWireType || 'PSM';
    }
  }
}

/**
 * Get connector specification string
 * @param {Object} connector - The connector object
 * @returns {string} Connector specification
 */
function getConnectorSpec(connector) {
  if (!connector) return 'Unknown';
  
  const parts = [];
  
  if (connector.brand) parts.push(connector.brand);
  if (connector.connector) parts.push(connector.connector);
  if (connector.gender) parts.push(connector.gender);
  if (connector.variant) parts.push(connector.variant);
  
  return parts.join(' ') || connector.id || 'Unknown';
}

/**
 * Get connector description
 * @param {Object} connector - The connector object
 * @returns {string} Connector description
 */
function getConnectorDescription(connector) {
  if (!connector) return 'Unknown';
  
  let description = 'Unknown connector';
  
  if (connector.id) {
    // Get the proper description from the compatibility matrix
    const matrixEntry = compatibilityMatrix[connector.id];
    if (matrixEntry?.description) {
      description = matrixEntry.description;
    } else if (matrixEntry?.name) {
      description = matrixEntry.name;
    } else if (connector.description) {
      description = connector.description;
    } else if (connector.label) {
      description = connector.label;
    } else if (connector.name) {
      description = connector.name;
    } else {
      description = connector.id;
    }
  } else {
    // Fallback for connectors without ID
    description = connector.label || connector.description || connector.connector || connector.id || 'Unknown';
  }
  
  return description;
}

/**
 * Compress BOM data for URL storage
 * @param {Object} bom - The BOM object
 * @returns {string} Compressed, URL-safe string
 */
function compressBOMForURL(bom) {
  try {
    // Create a minimal version for URL storage
    const minimalBOM = {
      id: bom.id,
      sku: bom.sku,
      name: bom.name,
      timestamp: bom.timestamp,
      config: bom.config
    };
    // Convert to JSON and compress using lz-string
    const jsonString = JSON.stringify(minimalBOM);
    const compressed = compressToEncodedURIComponent(jsonString);
    return compressed;
  } catch (error) {
    console.error('Error compressing BOM:', error);
    throw new Error('Failed to compress BOM data');
  }
}

/**
 * Decompress BOM data from URL
 * @param {string} compressedData - The compressed BOM data
 * @returns {Object} Decompressed BOM object
 */
function decompressBOMFromURL(compressedData) {
  try {
    // Decompress the data using lz-string
    const jsonString = decompressFromEncodedURIComponent(compressedData);
    if (!jsonString) throw new Error('Decompression returned null/empty string');
    const bom = JSON.parse(jsonString);
    return bom;
  } catch (error) {
    console.error('Error decompressing BOM:', error);
    throw new Error('Failed to decompress BOM data');
  }
}

/**
 * Export BOM as JSON file
 * @param {Object} config - The cable configuration
 * @param {string} customName - Optional custom name
 * @returns {Promise<Object>} Export result
 */
export async function exportBOMAsJSON(config, customName = null) {
  try {
    const bom = await generateBOM(config, customName);
    const sku = generateSKU(config);
    const filename = `NLFX_BOM_${sku}_${Date.now()}.json`;
    
    // Create and download the JSON file
    const blob = new Blob([JSON.stringify(bom, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return {
      success: true,
      filename: filename,
      bom: bom
    };
  } catch (error) {
    console.error('Error exporting BOM as JSON:', error);
    throw new Error('Failed to export BOM');
  }
}

/**
 * Validate if a configuration can generate a BOM
 * @param {Object} config - The cable configuration
 * @returns {boolean} True if BOM can be generated
 */
export function canGenerateBOM(config) {
  if (!config) return false;
  
  // Check if we have basic cable information
  if (!config.type) return false;
  
  // Check if we have connectors
  if (config.style === 'end-to-end') {
    return config.connectors && (config.connectors.endA || config.connectors.endB);
  } else if (config.hybridConnectors?.connectors) {
    const connectors = config.hybridConnectors.connectors;
    return Object.values(connectors).some(connector => connector && connector.id);
  }
  
  return false;
} 